import { formatMoney } from '../utils/currencyUtil';
import { posProductCustomizations, posProductExtras, posExtraQuantity } from '../utils/posCustomizationUtil';
import { POS_CUSTOMIZATION_TYPES, POS_CUSTOMIZATION_TEXT_MAX_LENGTH, POS_EXTRA_QTY_MAX } from '../constants/posConst';
import '../css/Pos.css';

/**
 * The till's side of customization options: one question per option, answered
 * before the item goes into the order.
 *
 * Choices are tappable chips rather than a dropdown — on a counter tablet a
 * sugar level is one tap, not two plus a scroll. A single-pick option behaves
 * like a radio that can also be tapped off again; a multi-pick option toggles
 * each chip on its own.
 *
 * EXTRAS follow the options, each with its own count (0 by default), because
 * a customer asks for "two extra pearls", not for a yes or no.
 *
 * Controlled: the panel owns `picks` and resets it per product.
 *
 * @param {object} props
 * @param {object} props.product - the product whose options and extras to ask
 * @param {Object<string, string[]|string|number>} props.picks - see posCustomizationUtil
 * @param {(picks: Object<string, string[]|string|number>) => void} props.onChange
 * @param {object} props.config - for the currency symbol
 * @param {boolean} [props.disabled]
 * @returns {JSX.Element|null}
 */
function PosCustomizationPicker({ product, picks, onChange, config, disabled = false }) {
  const options = posProductCustomizations(product);
  const extras = posProductExtras(product);
  if (options.length === 0 && extras.length === 0) return null;

  /**
   * Steps one extra's count, held inside 0..POS_EXTRA_QTY_MAX.
   *
   * @param {string} extraId
   * @param {-1|1} delta
   * @returns {void}
   */
  const stepExtra = (extraId, delta) => {
    const next = posExtraQuantity(posExtraQuantity(picks?.[extraId]) + delta);
    onChange({ ...picks, [extraId]: next });
  };

  /**
   * Taps one choice on or off.
   *
   * @param {object} option - the option the chip belongs to
   * @param {string} name - the choice tapped
   * @returns {void}
   */
  const toggleChoice = (option, name) => {
    const current = Array.isArray(picks?.[option.id]) ? picks[option.id] : [];
    const isOn = current.includes(name);
    let next;
    if (option.multiple) next = isOn ? current.filter((value) => value !== name) : [...current, name];
    else next = isOn ? [] : [name];
    onChange({ ...picks, [option.id]: next });
  };

  return (
    <div className="pos-opt-picker">
      {options.map((option) => {
        const chosen = Array.isArray(picks?.[option.id]) ? picks[option.id] : [];
        return (
          <fieldset key={option.id} className="pos-opt-question">
            <legend>
              {option.label}
              <span className="pos-opt-question-note">
                {option.required ? 'Required' : 'Optional'}
                {option.multiple ? ' · pick any' : ''}
              </span>
            </legend>

            {option.type === POS_CUSTOMIZATION_TYPES.TEXT ? (
              <input
                type="text"
                className="pos-opt-text"
                value={typeof picks?.[option.id] === 'string' ? picks[option.id] : ''}
                onChange={(e) => onChange({ ...picks, [option.id]: e.target.value })}
                maxLength={POS_CUSTOMIZATION_TEXT_MAX_LENGTH}
                placeholder="Type the answer"
                aria-label={option.label}
                disabled={disabled}
              />
            ) : (
              <div className="pos-opt-chips">
                {option.choices.map((choice) => {
                  const on = chosen.includes(choice.name);
                  return (
                    <button
                      key={choice.name}
                      type="button"
                      className={`pos-opt-chip${on ? ' active' : ''}`}
                      aria-pressed={on}
                      onClick={() => toggleChoice(option, choice.name)}
                      disabled={disabled}
                    >
                      {choice.name}
                      {choice.price > 0 && (
                        <span className="pos-opt-chip-price">+{formatMoney(choice.price, config)}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </fieldset>
        );
      })}

      {extras.length > 0 && (
        <fieldset className="pos-opt-question">
          <legend>
            Extras
            <span className="pos-opt-question-note">Optional · each one adds its price</span>
          </legend>
          <ul className="pos-extra-list">
            {extras.map((item) => {
              const qty = posExtraQuantity(picks?.[item.id]);
              return (
                <li key={item.id} className={`pos-extra-row${qty > 0 ? ' active' : ''}`}>
                  <span className="pos-extra-text">
                    <strong>{item.label}</strong>
                    {item.description && <em>{item.description}</em>}
                    <span className="pos-opt-chip-price">+{formatMoney(item.price, config)} each</span>
                  </span>
                  <span className="pos-extra-stepper">
                    <button
                      type="button"
                      onClick={() => stepExtra(item.id, -1)}
                      disabled={disabled || qty <= 0}
                      aria-label={`One less ${item.label}`}
                    >
                      &minus;
                    </button>
                    <span aria-live="polite">{qty}</span>
                    <button
                      type="button"
                      onClick={() => stepExtra(item.id, 1)}
                      disabled={disabled || qty >= POS_EXTRA_QTY_MAX}
                      aria-label={`One more ${item.label}`}
                    >
                      +
                    </button>
                  </span>
                </li>
              );
            })}
          </ul>
        </fieldset>
      )}
    </div>
  );
}

export default PosCustomizationPicker;
